import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { fromZonedTime } from 'date-fns-tz';
import config from 'garaje/config/environment';
import type MapFloorModel from 'garaje/models/map-floor';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type SessionService from 'garaje/services/session';
import type StateService from 'garaje/services/state';

import type { ModelResponse } from './route';

/**
 * Resource types the filter dropdown offers. Each entry pairs a human
 * label (what the user sees) with the FPS layer id that `postFilterUpdate`
 * sends to floor-plan-fe — matching what the render envelope emits in each
 * object's `layerIdMemberships` (e.g. "rt-desk", "rt-mothers_room").
 *
 * `null` layerId represents "All resources" (no filter). Matches
 * live/show's DEFAULT_RESOURCE_FILTER = 'all-resources' semantics.
 */
const RESOURCE_TYPES: Array<{ label: string; layerId: string | null }> = [
  { label: 'All resources', layerId: null },
  { label: 'Desks', layerId: 'rt-desk' },
  { label: 'Rooms', layerId: 'rt-room' },
  { label: 'Parking', layerId: 'rt-parking' },
  { label: 'Accessible', layerId: 'rt-accessible' },
  { label: 'AED', layerId: 'rt-aed' },
  { label: 'Break room', layerId: 'rt-break_room' },
  { label: 'Cafe', layerId: 'rt-cafe' },
  { label: 'Delivery area', layerId: 'rt-delivery_area' },
  { label: 'Elevator', layerId: 'rt-elevator' },
  { label: 'Emergency exit', layerId: 'rt-emergency_exit' },
  { label: 'Fire extinguisher', layerId: 'rt-fire_extinguisher' },
  { label: 'First aid kit', layerId: 'rt-first_aid_kit' },
  { label: 'Kitchen', layerId: 'rt-kitchen' },
  { label: "Mother's room", layerId: 'rt-mothers_room' },
  { label: 'Printer', layerId: 'rt-printer' },
  { label: 'Restroom', layerId: 'rt-restroom' },
  { label: 'Stairs', layerId: 'rt-stairs' },
  { label: 'Visitor area', layerId: 'rt-visitor_area' },
];

/** Sentinel for the "All resources" option (matches live/show). */
const ALL_RESOURCES = 'all-resources';

/** Default all-day time window when no workplaceDays hours are known. */
const DEFAULT_TIME: { startTime: string; endTime: string } = {
  startTime: '00:00:00',
  endTime: '23:59:00',
};

export default class SpacesMapsLiveNewController extends Controller {
  @service declare state: StateService;
  @service declare session: SessionService;

  declare model: ModelResponse;

  // ---------------------------------------------------------------------------
  // Query params — mirror the shape live/show uses so paste-URL behaviour
  // matches. Only include controls that actually feed the iframe today;
  // `searchTerm` is intentionally excluded until search is wired end-to-end
  // (a bookmarked URL restoring a search term the UI can't act on would be
  // confusing).
  // ---------------------------------------------------------------------------

  queryParams = ['selectedFloorId', 'selectedResourceFilter'];

  // ---------------------------------------------------------------------------
  // Tracked state
  // ---------------------------------------------------------------------------

  @tracked searchTerm = '';
  @tracked selectedFloorId: string | null = null;
  /**
   * Currently-selected resource-type filter label (e.g. "Desks", or
   * "All resources"). Defaults to "All resources" so the dropdown has
   * an initial selection matching live/show's default.
   */
  @tracked selectedResourceFilter: string = ALL_RESOURCES;
  @tracked selectedNeighborhoods: NeighborhoodModel[] = [];

  // Date/time picker state
  @tracked selectedDate: Date = new Date();
  @tracked allDayEnabled = true;
  @tracked selectedTimeObject: { startTime: string; endTime: string } = { ...DEFAULT_TIME };

  /**
   * Iframe DOM node — captured on `{{did-insert}}` in the template so
   * `postFilterUpdate` can talk to its contentWindow without a global
   * `document.querySelector` (matches sibling routes' data-DOM
   * boundary — controllers shouldn't reach into the DOM).
   */
  @tracked iframeElement: HTMLIFrameElement | null = null;

  // ---------------------------------------------------------------------------
  // Derived data for template
  // ---------------------------------------------------------------------------

  get sortedMapFloors(): MapFloorModel[] {
    const floors = this.model?.mapFloors ?? [];
    return [...floors].sort((a, b) => (a.ordinality || 0) - (b.ordinality || 0)).filter((f) => f.rasterImageUrl);
  }

  get placedResourceTypes(): string[] {
    return RESOURCE_TYPES.map((r) => r.label);
  }

  /**
   * Resolve the FPS layer id for the current selection. `null` means
   * "no filter, show everything" (also the value we send over
   * postMessage for the "All resources" option).
   */
  get selectedResourceLayerId(): string | null {
    const entry = RESOURCE_TYPES.find((r) => r.label === this.selectedResourceFilter);
    return entry?.layerId ?? null;
  }

  get currentFloor(): MapFloorModel | null {
    if (!this.selectedFloorId) return this.sortedMapFloors[0] ?? null;
    return this.sortedMapFloors.find((f) => f.id === this.selectedFloorId) ?? this.sortedMapFloors[0] ?? null;
  }

  get neighborhoods(): NeighborhoodModel[] {
    return this.model?.neighborhoods ?? [];
  }

  get workplaceDays(): WorkplaceDayModel[] {
    return this.model?.workplaceDays ?? [];
  }

  // ---------------------------------------------------------------------------
  // Iframe URL — recomputes whenever any tracked filter changes, so the
  // iframe reloads with fresh context. floor-plan-fe reads:
  //   ?location=<id>&floor=<id>&start_time=<iso>&end_time=<iso>
  // Filter params (resource, neighborhoods) are NOT in the URL — they flow
  // via postMessage (postFilterUpdate) so the iframe doesn't reload on
  // every toolbar interaction.
  // ---------------------------------------------------------------------------

  get floorPlanFeUrl(): string {
    const base = config.floorPlanFeUrl;
    if (!base) return '';

    const locationId = this.state.currentLocation?.id;
    if (!locationId) return base;

    const { startIso, endIso } = this.computeTimeWindow();

    const entries: [string, string][] = [
      ['location', locationId],
      ['start_time', startIso],
      ['end_time', endIso],
    ];
    if (this.currentFloor?.id) entries.push(['floor', this.currentFloor.id]);

    return `${base}?${new URLSearchParams(entries).toString()}`;
  }

  /**
   * Combine selectedDate + selectedTimeObject (or full-day window if
   * allDayEnabled) into ISO-8601 start/end strings for the iframe URL.
   *
   * Interprets the wall-clock time in the LOCATION's timezone (via
   * date-fns-tz's `fromZonedTime`), not the browser's local time. Without
   * this a NYC admin viewing a London office was sending a window offset
   * by the browser-vs-location delta — the map surfaced availability for
   * the wrong hours.
   */
  computeTimeWindow(): { startIso: string; endIso: string } {
    const startTime = this.allDayEnabled ? DEFAULT_TIME.startTime : this.selectedTimeObject.startTime;
    const endTime = this.allDayEnabled ? DEFAULT_TIME.endTime : this.selectedTimeObject.endTime;
    const tz = this.state.currentLocation?.timezone;

    const start = zonedIso(this.selectedDate, startTime, tz);
    const end = zonedIso(this.selectedDate, endTime, tz);
    return { startIso: start, endIso: end };
  }

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  @action
  handleSearchInputChange(value: string): void {
    this.searchTerm = value;
  }

  @action
  clearSearch(): void {
    this.searchTerm = '';
  }

  @action
  onMapFloorChange(floor: MapFloorModel): void {
    this.selectedFloorId = floor?.id ?? null;
  }

  @action
  onResourceFilterSelect(type: string | null): void {
    this.selectedResourceFilter = type ?? ALL_RESOURCES;
    this.postFilterUpdate();
  }

  @action
  handleSelectNeighborhood(neighborhoods: NeighborhoodModel[]): void {
    this.selectedNeighborhoods = neighborhoods ?? [];
    this.postFilterUpdate();
  }

  /**
   * Capture the iframe DOM node when the template inserts it. Wired via
   * `{{did-insert this.registerIframe}}` so we don't have to reach into
   * the DOM via a global selector.
   */
  @action
  registerIframe(el: HTMLIFrameElement): void {
    this.iframeElement = el;
  }

  /**
   * Iframe finished loading (fresh navigation). Push the current filter
   * state so any user-driven change that landed before load isn't lost
   * to a silent no-op inside postFilterUpdate.
   */
  @action
  handleIframeLoad(): void {
    this.postRenderContext();
    this.postFilterUpdate();
  }

  /**
   * Push the current OAuth access token to the floor-plan-fe iframe via
   * postMessage. FPS's `/actions/*` endpoints require
   * `Authorization: Bearer <token>` and floor-plan-fe reads the token
   * out of `RenderContext.accessToken`. Session cookies still work for
   * `/render` but Bearer is the intended auth for mutations.
   *
   * Called on iframe load (via `handleIframeLoad`); the FE seeds the
   * value into a module-level holder that `postAction` reads at call
   * time. If the session isn't authenticated (e.g. logout mid-view),
   * no message is posted and the FE keeps its last-known token.
   */
  postRenderContext(): void {
    const win = this.iframeElement?.contentWindow;
    if (!win) return;

    const base = config.floorPlanFeUrl;
    if (!base) return;

    let targetOrigin: string;
    try {
      targetOrigin = new URL(base).origin;
    } catch {
      return;
    }

    const locationId = this.state.currentLocation?.id;
    if (!locationId) return;

    // Ember Simple Auth stores the OAuth token at
    // session.data.authenticated.access_token — same read path used by
    // ajax-fetch.ts to attach the header on every HTTP call.
    const accessToken = this.session.isAuthenticated
      ? (this.session.data?.authenticated as { access_token?: string } | undefined)?.access_token
      : undefined;

    win.postMessage(
      {
        type: 'render-context',
        context: {
          location: Number(locationId),
          accessToken,
        },
      },
      targetOrigin,
    );
  }

  /**
   * Push the current filter state to the floor-plan-fe iframe via
   * postMessage. Applied client-side inside the iframe (maplibre
   * setFilter for resource type; source.setData for neighborhood
   * highlights) — no iframe reload.
   *
   * No-op when the iframe hasn't loaded yet; `handleIframeLoad` re-runs
   * postFilterUpdate on load so any early state is applied.
   */
  postFilterUpdate(): void {
    const win = this.iframeElement?.contentWindow;
    if (!win) return;

    const base = config.floorPlanFeUrl;
    if (!base) return;

    // `new URL(base)` throws on a malformed value; guard so a future
    // env misconfig doesn't kill every filter change.
    let targetOrigin: string;
    try {
      targetOrigin = new URL(base).origin;
    } catch {
      return;
    }

    win.postMessage(
      {
        type: 'filter-update',
        filters: {
          neighborhoods: this.selectedNeighborhoods.map((n) => `nbhd-${n.id}`),
          resourceType: this.selectedResourceLayerId,
        },
      },
      targetOrigin,
    );
  }

  @action
  searchNeighborhoods(term: string): NeighborhoodModel[] {
    const t = term.toLowerCase();
    return this.neighborhoods.filter((n) => n.name.toLowerCase().includes(t));
  }

  @action
  onDateSelect(date: Date): void {
    this.selectedDate = date;
  }

  @action
  handleAllDayToggle(enabled: boolean): void {
    this.allDayEnabled = enabled;
  }

  @action
  handlePartialTimeChange(next: { startTime: string; endTime: string }): void {
    this.selectedTimeObject = next;
  }
}

/**
 * Combine a Date (from the calendar picker) with an "HH:mm:ss" wall-clock
 * time, interpret the result in the given IANA timezone, and return the
 * absolute-instant ISO-8601 string.
 *
 * Falls back to the browser's local timezone when `tz` is missing (e.g.
 * currentLocation still loading) — the caller renders the same window
 * anyway once the location resolves and the getter re-runs.
 */
function zonedIso(date: Date, hhmmss: string, tz: string | undefined): string {
  const [h, m, s] = hhmmss.split(':').map((n) => parseInt(n, 10));
  const wall = new Date(date);
  wall.setHours(h ?? 0, m ?? 0, s ?? 0, 0);
  if (!tz) return wall.toISOString();
  // `wall` currently reads as the browser's local wall-clock. We want the
  // SAME wall-clock but interpreted in `tz`. fromZonedTime treats the input
  // as a local time in `tz` and returns the corresponding UTC instant.
  return fromZonedTime(wall, tz).toISOString();
}
